import axios, { AxiosResponse } from "axios";
import { BASE_URL } from "./index";
import { useMutation } from "@tanstack/react-query";
import { useSignIn } from "react-auth-kit";
import { useModeStore } from "../stores/store-settings";

/**
 * Hook to handle user login.
 *
 * On successful login, it signs the user in with the auth kit and redirects to the home page.
 *
 * @returns {Object} A React Query mutation object to perform login and handle login state.
 */
export const useLogin = () => {
  const signIn = useSignIn();
  const { setNotification, setEmailNotification } = useModeStore();

  return useMutation<AxiosResponse, Error, { email: any; password: any }>(
    async ({ email, password }) => {
      const url = `${BASE_URL}/auth/login`;
      const response: AxiosResponse = await axios.post(url, {
        email,
        password,
      });
      return response;
    },
    {
      onSuccess: (response) => {
        const expiresInDays = 3650;
        const expiresInSeconds = expiresInDays * 24 * 60 * 60;
        const { data } = response.data;
        signIn({
          token: response.data.token,
          expiresIn: expiresInSeconds,
          tokenType: "Bearer",
          authState: {
            token: data.access_token,
            id: data.user.id,
            fullName: data.user.first_name + " " + data.user.last_name,
            status: data.user.status,
            email: data.user.email,
            access_token: data.access_token,
            role: data.user.role,
            roleKey: data.user.roleKey,
            clientUserId: data.user.associate_account,
            emailVerifiedAt: data.user.email_verified_at
          },
        });
        setNotification(data.user.is_notification_enabled);
        setEmailNotification(data.user.is_email_notification_enabled);

        setTimeout(() => {
          window.location.replace("/");
        }, 500);
      },
    }
  );
};

/**
 * Hook to handle forgot password functionality.
 *
 * Sends a reset password link to the user's email.
 *
 * @returns {Object} A React Query mutation object to request a password reset email.
 */
export const useForgotPassword = () => {
  return useMutation<AxiosResponse, Error, { email: any }>(
    async ({ email }) => {
      const url = `${BASE_URL}/auth/send-reset-password-link`;
      const response: AxiosResponse = await axios.post(url, {
        email,
      });
      return response;
    }
  );
};


/**
 * Hook to reset the user's password.
 *
 * @returns {Object} A React Query mutation object to reset the password using a token.
 */
export const useResetPassword = () => {
  return useMutation<
    AxiosResponse,
    Error,
    { token: string; password: string; password_confirmation: string }
  >(async ({ token, password, password_confirmation }) => {
    const url = `${BASE_URL}/auth/reset-password`;
    const response: AxiosResponse = await axios.post(url, {
      token,
      password,
      password_confirmation,
    });
    return response;
  });
};

/**
 * Hook to verify a user's email address.
 *
 * @returns {Object} A React Query mutation object to verify user email through verification link.
 */
export const useEmailVerification = () => {
  return useMutation<
    AxiosResponse,
    Error,
    { id: string; hash: string; expires: string; signature: string }
  >(async ({ id, hash, expires, signature }) => {
    const url = `${BASE_URL}/auth/email/verify/${id}/${hash}?expires=${expires}&signature=${signature}`;
    const response: AxiosResponse = await axios.get(url, {
      headers: {
        Accept: "application/json",
      },
    });
    return response;
  });
};

/**
 * Hook to set a new password for a user.
 *
 * @returns {Object} A React Query mutation object to set a new password.
 */
export const useSetPassword = () => {
  return useMutation<
    AxiosResponse,
    Error,
    { email: string; password: string; password_confirmation: string }
  >(async ({ email, password, password_confirmation }) => {
    const url = `${BASE_URL}/auth/set-password`;
    const response: AxiosResponse = await axios.post(url, {
      email,
      password,
      password_confirmation,
    });
    return response;
  });
};
